<template>
  <woot-modal :show="show" :on-close="onClose">
    <woot-modal-header :header-title="modalTitle" />
    <form class="flex flex-col modal-content" @submit.prevent="onSubmit">
      <div class="space-y-5">
          <div class="space-y-2">
            <woot-input
              v-model="form.name"
              :label="$t('IMAGE_LIBRARY.FORM.NAME')"
              :required="true"
              :placeholder="$t('IMAGE_LIBRARY.FORM.NAME_PLACEHOLDER')"
              :error="v$.form.name.$error ? $t('IMAGE_LIBRARY.FORM.NAME_ERROR') : ''"
              :help-text="!v$.form.name.$error ? $t('IMAGE_LIBRARY.FORM.NAME_HELP') : ''"
              :class="{ error: v$.form.name.$error }"
              maxlength="200"
              @blur="v$.form.name.$touch"
            />
          </div>

          <div class="space-y-2">
            <FormLabel
              :label="$t('IMAGE_LIBRARY.FORM.DESCRIPTION')"
              :required="true"
              :has-error="v$.form.description.$error"
            />
            <TextArea
              v-model="form.description"
              name="image-library-description"
              :placeholder="$t('IMAGE_LIBRARY.FORM.DESCRIPTION_PLACEHOLDER')"
              :message="v$.form.description.$error ? $t('IMAGE_LIBRARY.FORM.DESCRIPTION_ERROR') : ''"
              :message-type="v$.form.description.$error ? 'error' : 'info'"
              :resize="true"
              auto-height
              :min-height="'60px'"
              rows="3"
              :disabled="isSubmitting"
              @blur="v$.form.description.$touch"
            />
            <div
              v-if="!v$.form.description.$error"
              class="text-xs text-slate-500 dark:text-slate-400"
            >
              {{ $t('IMAGE_LIBRARY.FORM.DESCRIPTION_HELP') }}
            </div>
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ $t('IMAGE_LIBRARY.FORM.CATEGORY') }}
            </label>
        <CategorySelector
          :key="`category-selector-${library?.id || 'new'}-${businessCategories.length}`"
          v-model="form.business_category_id"
          :categories="businessCategories"
          category-type="image_library"
          :placeholder="$t('IMAGE_LIBRARY.FORM.CATEGORY_PLACEHOLDER')"
          @category-created="onCategoryCreated"
          @category-updated="onCategoryUpdated"
        />
            <div class="text-xs text-slate-500 dark:text-slate-400">
              {{ $t('IMAGE_LIBRARY.FORM.CATEGORY_HELP') }}
            </div>
          </div>  
      </div>
      <div class="flex justify-center gap-3 pt-4">
        <woot-button type="button" variant="smooth" color-scheme="ghost" :disabled="isSubmitting" @click="onClose">
          {{ $t('COMMON.CANCEL') }}
        </woot-button>
        <woot-button type="submit" variant="smooth" color-scheme="success" :is-loading="isSubmitting" :disabled="!canSubmit" @click="onSubmit">
          {{ submitLabel }}
        </woot-button>
      </div>
    </form>
  </woot-modal>
</template>

<script>
import CategorySelector from './CategorySelector.vue';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import FormLabel from 'dashboard/components/widgets/forms/FormLabel.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

export default {
  name: 'LibraryModal',
  components: { CategorySelector, FormLabel, TextArea },
  props: {
    show: { type: Boolean, default: false },
    accountId: { type: [String, Number], required: true },
    library: { type: Object, default: null },
    businessCategories: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'saved', 'category-created', 'category-updated'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isSubmitting: false,
      form: {
        name: '', 
        description: '', 
        category: '', 
        business_category_id: null,
      },
      descriptionError: '',
    }
  },
  validations() {
    return {
      form: {
        name: { required, maxLength: maxLength(200) },
        description: { required },
      },
    };
  },
  computed: {
    modalTitle() {
      return this.library ? this.$t('IMAGE_LIBRARY.EDIT_FOLDER') : this.$t('IMAGE_LIBRARY.ADD_FOLDER')
    },
    isValid() {
      return this.form.name.trim().length > 0 && this.form.description.trim().length > 0
    },
    canSubmit() {
      return this.isValid && !this.isSubmitting
    },
    submitLabel() {
      return this.library ? this.$t('COMMON.UPDATE') : this.$t('COMMON.SAVE')
    }
  },
  watch: {
    library: {
      immediate: true,
      handler(v) {
        this.form = v ? { 
          name: v.name, 
          description: v.description, 
          category: v.category, 
          business_category_id: v.business_category_id || null
        } : { 
          name: '', 
          description: '', 
          category: '', 
          business_category_id: null
        }
      }
    }
  },
  methods: {
    resetForm() {
      this.form = {
        name: '', 
        description: '', 
        category: '', 
        business_category_id: null
      }
      this.descriptionError = ''
      this.v$.$reset()
    },
    
    onClose() { 
      this.resetForm()
      this.$emit('close') 
    },
    
    onCategoryCreated(newCategory) {
      this.$emit('category-created', newCategory);
    },

    onCategoryUpdated() {
      this.$emit('category-updated');
    },
    
    async onSubmit() {
      if (!this.isValid || this.isSubmitting) {
        return
      }
      
      try {
        this.isSubmitting = true
        if (this.library) {
          await window.axios.put(`/api/v1/accounts/${this.accountId}/image_libraries/${this.library.id}`, { image_library: this.form })
        } else {
          await window.axios.post(`/api/v1/accounts/${this.accountId}/image_libraries`, { image_library: this.form })
        }
        this.$emit('saved')
        this.resetForm()
      } finally {
        this.isSubmitting = false
      }
    },
  }
}
</script>
